/**
 * Icon action with a hover tooltip, or — with `showLabel` — a glyph plus a visible label.
 *
 * The feed card's and the popup bar's actions, plus the popup's pager, are square glyph buttons
 * with no label, so the name has to arrive on hover: this owns the hover state and the anchor ref
 * and hands both to the shared `HoverTip` (portaled and anchored like every other overlay, §12).
 * Renders the existing `.table-action .table-action--icon` pair, or an `<a>` when `href` is given.
 *
 * `showLabel` drops the square variant and puts the name on screen instead of on hover — for the
 * one action in those bars a user reaches for by name ("Open on LinkedIn").
 */

import { useRef, useState, type ReactNode } from 'react';
import { HoverTip } from './HoverTip';

export type IconActionProps = {
  /** Tooltip text and accessible name; the visible label when `showLabel` is set. */
  label: string;
  icon: ReactNode;
  /** Renders an `<a target="_blank" rel="noreferrer noopener">` instead of a `<button>`. */
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  /**
   * Render `label` beside the glyph instead of in a hover tip.
   *
   * Only for a **static** label: a swapping one ("Copied", "Saving to Notion…") would resize the
   * control mid-interaction.
   */
  showLabel?: boolean;
};

export function IconAction({
  label,
  icon,
  href,
  onClick,
  disabled = false,
  showLabel = false,
}: IconActionProps) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [hovered, setHovered] = useState(false);

  // One ref for either element type, so the two branches share the anchor.
  const setAnchor = (node: HTMLElement | null) => {
    anchorRef.current = node;
  };
  // A labelled action needs no hover state at all: the tip would repeat text already on screen
  // 6px away, and tracking hover for a tip that never opens is pure re-render churn.
  const hover = showLabel
    ? {}
    : {
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
      };
  // A disabled button fires no mouse events in Chrome, so a hint opened just before the button
  // went disabled would never be dismissed: gate it on `disabled` rather than wait for a leave.
  const tip = showLabel ? null : (
    <HoverTip open={hovered && !disabled} anchorRef={anchorRef} text={label} />
  );
  const className = showLabel ? 'table-action' : 'table-action table-action--icon';
  // `aria-label` *overrides* text content, so a visible label plus an aria-label would hand
  // assistive tech the same string through two mechanisms. Let the span be the accessible name.
  const ariaLabel = showLabel ? undefined : label;
  const content = (
    <>
      {icon}
      {showLabel ? <span className="table-action__label">{label}</span> : null}
    </>
  );

  if (href !== undefined) {
    return (
      <>
        <a
          className={className}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={ariaLabel}
          ref={setAnchor}
          {...hover}
        >
          {content}
        </a>
        {tip}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        ref={setAnchor}
        {...hover}
      >
        {content}
      </button>
      {tip}
    </>
  );
}

export default IconAction;
